import { useContext, useState, useRef, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react'
import { FontIcon, Stack, TextField, IconButton, Spinner, SpinnerSize, ITextField, Dropdown, IDropdownOption } from '@fluentui/react'
import { SendRegular, DismissRegular, CheckmarkCircleRegular, EditRegular, AttachRegular, StopRegular } from '@fluentui/react-icons'

import Send from '../../assets/Send.svg'

import styles from './QuestionInput.module.css'
import { ChatMessage } from '../../api'
import { authenticatedFetch } from '../../utils/authApi'
import { AppStateContext } from '../../state/AppProvider'
import { resizeImage } from '../../utils/resizeImage'
import { useAsyncUpload } from '../../hooks/useAsyncUpload'
import { UploadProgressIndicator } from '../UploadProgress/UploadProgressIndicator'
import { debugLog } from '../../utils/debugLogger'

export interface QuestionInputRef {
  openFileDialog: () => void;
}

interface UploadedFile {
  id?: string;
  fileId?: string; // For async upload tracking
  file?: File; // Make optional to handle documents loaded from backend
  preview?: string;
  type: 'image' | 'document';
  uploading?: boolean;
  uploaded?: boolean;
  filename?: string;
  reused?: boolean; // Flag for reused documents
  // Multi-sheet Excel support
  available_sheets?: Array<{ name: string; rows: number; columns: number; column_names?: string[] }>;
  sheet_name?: string; // Currently selected sheet
}

interface Props {
  onSend: (question: ChatMessage['content'], id?: string, uploadedFiles?: UploadedFile[]) => void
  disabled: boolean
  placeholder?: string
  clearOnSend?: boolean
  conversationId?: string
  onFileUploadSuccess?: (fileMetas: any[]) => void
  onFileRemoved?: (fileId: string) => void
  clearFiles?: boolean
  externalUploadedFiles?: UploadedFile[]
  onUploadedFilesChange?: (files: UploadedFile[] | ((prev: UploadedFile[]) => UploadedFile[])) => void
  onFileDialogOpen?: () => void
  hideFilePreview?: boolean
  isLoading?: boolean  // NEW: Track if request is in progress
  onStop?: () => void  // NEW: Stop current request
}

export const QuestionInput = forwardRef<QuestionInputRef, Props & { value?: string, onChange?: (ev: any, newValue?: string) => void, onNewChat?: () => void, onFileUploadSuccess?: (fileMetas: any[]) => void, onFileRemoved?: (fileId: string) => void, clearFiles?: boolean, externalUploadedFiles?: UploadedFile[], onUploadedFilesChange?: (files: UploadedFile[] | ((prev: UploadedFile[]) => UploadedFile[])) => void, onFileDialogOpen?: () => void, hideFilePreview?: boolean, isLoading?: boolean, onStop?: () => void }>(({ onSend, disabled, placeholder, clearOnSend, conversationId, value, onChange, onNewChat, onFileUploadSuccess, onFileRemoved, clearFiles, externalUploadedFiles, onUploadedFilesChange, onFileDialogOpen, hideFilePreview = false, isLoading = false, onStop }, ref) => {
  const [internalQuestion, setInternalQuestion] = useState<string>('')
  const isControlled = value !== undefined && onChange !== undefined;
  const question = isControlled ? value : internalQuestion;
  const setQuestion = isControlled ? (v: string) => onChange!(undefined, v) : setInternalQuestion;
  const [base64Image, setBase64Image] = useState<string | null>(null);
  const [internalUploadedFiles, setInternalUploadedFiles] = useState<UploadedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textFieldRef = useRef<ITextField>(null);

  // Initialize async upload hook
  const { startUpload, getAllUploads, getUploadProgress, removeUpload, cleanup } = useAsyncUpload();

  // Use external uploaded files if provided, otherwise use internal state
  const uploadedFiles = externalUploadedFiles || internalUploadedFiles;
  
  const updateUploadedFiles = (updater: (prev: UploadedFile[]) => UploadedFile[]) => {
    if (onUploadedFilesChange) {
      // Use the updater function directly - don't pass current uploadedFiles
      onUploadedFilesChange(updater);
    } else {
      setInternalUploadedFiles(updater);
    }
  };

  const appStateContext = useContext(AppStateContext)
  const OYD_ENABLED = appStateContext?.state.frontendSettings?.oyd_enabled || false;

  // RTL detection for user input with improved mixed content handling
  const isInputRtl = useMemo(() => {
    // Count RTL and total alphabetic characters in user input
    const rtlChar = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\u0700-\u074F]/g;
    const alphabeticChar = /[\u0041-\u005A\u0061-\u007A\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\u0700-\u074F\u00C0-\u024F\u1E00-\u1EFF]/g;
    
    const rtlMatches = question.match(rtlChar) || [];
    const alphabeticMatches = question.match(alphabeticChar) || [];
    
    if (alphabeticMatches.length < 2) return false; // Lower minimum threshold
    
    const rtlPercentage = (rtlMatches.length / alphabeticMatches.length) * 100;
    
    // Improved logic for mixed content:
    // 1. If Hebrew is >30% of text, use RTL (lowered from 40%)
    // 2. Special case: if text starts with Hebrew word, prefer RTL
    // 3. If Hebrew chars > 3 and >30%, use RTL
    const startsWithHebrew = /^[\u0590-\u05FF]/.test(question.trim());
    const hasSignificantHebrew = rtlMatches.length > 3 && rtlPercentage > 30;
    const isRtl = rtlPercentage > 30 || (startsWithHebrew && rtlPercentage > 20);
    
    return isRtl;
  }, [question]);

  // Handle cursor positioning for RTL/LTR changes
  useEffect(() => {
    if (textFieldRef.current) {
      const fieldElement = textFieldRef.current as any;
      if (fieldElement && fieldElement._textField && fieldElement._textField.current) {
        const inputElement = fieldElement._textField.current as HTMLTextAreaElement;
        
        // Force recalculation of cursor position by setting direction
        inputElement.dir = isInputRtl ? 'rtl' : 'ltr';
        inputElement.style.direction = isInputRtl ? 'rtl' : 'ltr';
        inputElement.style.textAlign = isInputRtl ? 'right' : 'left';
        inputElement.style.unicodeBidi = 'plaintext';
        
        // Position cursor correctly for RTL/LTR
        // For RTL, cursor should be at the end of text (visually right side)
        // For LTR, cursor should also be at the end of text
        const cursorPosition = question.length;
        setTimeout(() => {
          inputElement.setSelectionRange(cursorPosition, cursorPosition);
          inputElement.focus();
        }, 0);
      }
    }
  }, [isInputRtl, question]);

  // Auto-focus the input field when component mounts and after sending messages
  useEffect(() => {
    const focusInput = () => {
      if (textFieldRef.current && !disabled) {
        // Use setTimeout to ensure the field is ready to receive focus
        setTimeout(() => {
          textFieldRef.current?.focus();
          
          // Additional mobile support - try to trigger keyboard
          if (window.innerWidth <= 768) {
            // For mobile devices, ensure the cursor is positioned at the end
            const inputElement = textFieldRef.current as any;
            if (inputElement && inputElement._textField) {
              const actualInput = inputElement._textField.current;
              if (actualInput) {
                actualInput.focus();
                // Move cursor to end of text
                const length = question.length;
                actualInput.setSelectionRange(length, length);
              }
            }
          }
        }, 100);
      }
    };

    // Focus on mount
    focusInput();
    
    // Focus after the disabled state changes (i.e., after sending a message)
    if (!disabled) {
      focusInput();
    }
  }, [disabled, question]);

  // Also focus when the question is cleared (after sending)
  useEffect(() => {
    if (!question.trim() && !disabled) {
      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 150); // Slightly longer delay for clearing
    }
  }, [question, disabled]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      await convertToBase64(file);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newFiles: UploadedFile[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isImage = file.type.startsWith('image/');
      
      let preview: string | undefined;
      if (isImage) {
        try {
          preview = await resizeImage(file, 800, 800);
        } catch (error) {
          console.error('Error resizing image:', error);
        }
      }
      
      newFiles.push({
        file,
        preview,
        type: isImage ? 'image' : 'document',
        uploading: true,
        uploaded: false,
        filename: file.name
      });
    }
    
    updateUploadedFiles(prev => [...prev, ...newFiles]);

    // Upload files using async upload system
    const currentUploadedFilesLength = uploadedFiles.length;
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileIndex = currentUploadedFilesLength + i;
      
      try {
        const fileId = await startUpload(file);
        
        if (fileId) {
          // Update the specific file with upload file ID
          updateUploadedFiles(prev => prev.map((f, index) => 
            index === fileIndex ? { 
              ...f, 
              id: fileId,
              fileId: fileId,
              uploading: true, // Will be updated by progress tracking
              uploaded: false,
              filename: file.name 
            } : f
          ));
        } else {
          // Mark file as failed
          updateUploadedFiles(prev => prev.map((f, index) => 
            index === fileIndex ? { 
              ...f, 
              uploading: false, 
              uploaded: false 
            } : f
          ));
        }
      } catch (error) {
        console.error('Error starting upload:', error);
        // Mark file as failed
        updateUploadedFiles(prev => prev.map((f, index) => 
          index === fileIndex ? { 
            ...f, 
            uploading: false, 
            uploaded: false 
          } : f
        ));
      }
    }
  };

  const removeFile = (index: number) => {
    const removedFile = uploadedFiles[index];
    updateUploadedFiles(prev => prev.filter((_, i) => i !== index));
    
    // Get the file ID - could be 'id' (after upload complete) or 'fileId' (during upload)
    const effectiveFileId = removedFile?.id || removedFile?.fileId;
    
    // Clean up async upload tracking (also cancels backend if in progress)
    if (effectiveFileId) {
      removeUpload(effectiveFileId);
    }
    
    // Notify parent component about file removal
    if (effectiveFileId && onFileRemoved) {
      onFileRemoved(effectiveFileId);
    }
  };

  // Clear all uploaded files
  const clearAllFiles = () => {
    // Clean up all async uploads
    uploadedFiles.forEach(file => {
      if (file.fileId) {
        removeUpload(file.fileId);
      }
    });
    updateUploadedFiles(() => []);
  };

  // Cleanup async uploads on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Clear files when clearFiles prop changes  
  useEffect(() => {
    if (clearFiles) {
      debugLog("[QuestionInput] Clearing files due to clearFiles prop");
      clearAllFiles();
    }
  }, [clearFiles]);

  const convertToBase64 = async (file: Blob) => {
    try {
      const resizedBase64 = await resizeImage(file, 800, 800);
      setBase64Image(resizedBase64);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleQuestionChange = (ev: any, newValue?: string) => {
    const value = newValue || '';
    setQuestion(value);
    
    if (onChange) {
      onChange(ev, value);
    }
  };

  const sendQuestion = () => {
    if (disabled || (!question.trim() && uploadedFiles.length === 0)) {
      return
    }

    // Send the question to backend for processing
    onSend(question, conversationId, uploadedFiles.filter(f => f.uploaded));

    if (clearOnSend) {
      setQuestion('')
      
      // Re-focus the input after clearing (with a small delay to ensure clearing is complete)
      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 50);
    }
  }

  // Handle button click - send message or stop generation
  const handleButtonClick = () => {
    if (isLoading) {
      // If loading, stop the current request
      onStop?.();
    } else {
      // If not loading, send the message
      sendQuestion();
    }
  }

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === 'Enter' && !ev.shiftKey && !(ev.nativeEvent?.isComposing === true)) {
      ev.preventDefault()
      if (!isLoading) {  // Only send if not currently loading
        sendQuestion()
      }
    }
  }

  const sendQuestionDisabled = disabled || (
    !question.trim() && 
    (uploadedFiles.length === 0 || uploadedFiles.some(f => f.uploading || !f.uploaded))
  )

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  useImperativeHandle(ref, () => ({
    openFileDialog
  }));

  // Expose the file dialog function to parent component
  useEffect(() => {
    if (onFileDialogOpen) {
      // This creates a way for parent to trigger file dialog
      (window as any).triggerFileDialog = openFileDialog;
    }
  }, [onFileDialogOpen]);

  const handleNewConversation = () => {
    onNewChat && onNewChat();
  };

  return (
    <div className={styles.questionInputWrapper}>
      {/* File Preview Area */}
      {!hideFilePreview && uploadedFiles.length > 0 && (
        <div className={styles.filePreviewContainer}>
          {uploadedFiles.map((file, index) => {
            const uploadProgress = file.fileId ? getUploadProgress(file.fileId) : null;
            
            return (
              <div key={index} className={styles.filePreviewItem}>
                {/* Show upload progress if available */}
                {uploadProgress && uploadProgress.status !== 'completed' && (
                  <div className={styles.uploadProgressSection}>
                    <UploadProgressIndicator upload={uploadProgress} compact />
                  </div>
                )}
                
                {file.type === 'image' && file.preview ? (
                  <div className={styles.imagePreview}>
                    <img src={file.preview} alt={file.filename || file.file?.name || 'Image'} className={styles.previewImage} />
                    <div className={styles.imageOverlay}>
                      <span className={styles.fileName}>{file.filename || file.file?.name}</span>
                      <div className={styles.fileActions}>
                        {(file.uploading || (uploadProgress && uploadProgress.status !== 'completed')) && (
                          <Spinner size={SpinnerSize.xSmall} />
                        )}
                        {(file.uploaded || (uploadProgress && uploadProgress.status === 'completed')) && !file.uploading && (
                          <CheckmarkCircleRegular className={styles.uploadSuccessIcon} />
                        )}
                        <IconButton
                          iconProps={{ iconName: 'Cancel' }}
                          className={styles.removeButton}
                          onClick={() => removeFile(index)}
                          ariaLabel="Remove file"
                          styles={{
                            root: {
                              height: 32,
                              width: 32,
                              border: '1px solid #d83b01',
                              borderRadius: '4px',
                              backgroundColor: '#fef2f2',
                              '&:hover': { backgroundColor: '#fee2e2', borderColor: '#b91c1c' }
                            },
                            icon: { fontSize: 14, color: '#d83b01' }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.documentPreview}>
                    <div className={styles.documentIcon}>
                      <FontIcon iconName="TextDocument" />
                    </div>
                    <div className={styles.documentInfo}>
                      <span className={styles.fileName}>{file.filename || file.file?.name}</span>
                      <span className={styles.fileSize}>
                        {file.file?.size ? `${(file.file.size / 1024 / 1024).toFixed(2)} MB` : 'Size unknown'}
                      </span>
                      {/* Sheet selector for Excel files with multiple sheets */}
                      {file.available_sheets && file.available_sheets.length > 1 && (
                        <Dropdown
                          placeholder="Select sheet"
                          selectedKey={file.sheet_name || file.available_sheets[0]?.name}
                          options={file.available_sheets.map(sheet => ({
                            key: sheet.name,
                            text: `${sheet.name} (${sheet.rows} rows)`,
                          }))}
                          onChange={(_ev, option) => {
                            if (option) {
                              updateUploadedFiles(prev => 
                                prev.map((f, i) => 
                                  i === index ? { ...f, sheet_name: option.key as string } : f
                                )
                              );
                            }
                          }}
                          styles={{
                            root: { marginTop: 4, minWidth: 140 },
                            dropdown: { fontSize: 12 },
                            title: { fontSize: 12, height: 24, lineHeight: 24 },
                            dropdownItem: { fontSize: 12, minHeight: 28 }
                          }}
                        />
                      )}
                    </div>
                    <div className={styles.fileActions}>
                      {(file.uploading || (uploadProgress && uploadProgress.status !== 'completed')) && (
                        <Spinner size={SpinnerSize.xSmall} />
                      )}
                      {(file.uploaded || (uploadProgress && uploadProgress.status === 'completed')) && !file.uploading && (
                        <CheckmarkCircleRegular className={styles.uploadSuccessIcon} />
                      )}
                      <IconButton
                        iconProps={{ iconName: 'Cancel' }}
                        className={styles.removeButton}
                        onClick={() => removeFile(index)}
                        ariaLabel="Remove file"
                        styles={{
                          root: {
                            height: 32,
                            width: 32,
                            border: '1px solid #d83b01',
                          borderRadius: '4px',
                          backgroundColor: '#fef2f2',
                          '&:hover': { backgroundColor: '#fee2e2', borderColor: '#b91c1c' }
                        },
                        icon: { fontSize: 14, color: '#d83b01' }
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
        </div>
      )}
      
      {/* Main Input Container */}
      <div className={`${styles.questionInputContainer} ${uploadedFiles.length > 0 ? styles.questionInputContainerWithFiles : ''}`}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.doc,.xlsx,.xls,.xlsm,.xlsb,.pptx,.ppt,.txt,.jpg,.jpeg,.png,.gif,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.ms-excel.sheet.macroEnabled.12,application/vnd.ms-excel.sheet.binary.macroEnabled.12,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-powerpoint,text/plain,image/*"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
        />
        
        <TextField
          className={`${styles.questionInputTextArea} ${isInputRtl ? 'rtl-input' : 'ltr-input'}`}
          placeholder={
            uploadedFiles.length > 0 && uploadedFiles.every(f => f.uploaded) 
              ? `Ask a question about your ${uploadedFiles.length} uploaded file${uploadedFiles.length > 1 ? 's' : ''}...`
              : (placeholder || 'Message Copilot')
          }
          multiline
          resizable={false}
          borderless
          value={question}
          onChange={handleQuestionChange}
          onKeyDown={onEnterPress}
          componentRef={textFieldRef}
          dir={isInputRtl ? 'rtl' : 'ltr'}
          styles={{
            field: {
              textAlign: isInputRtl ? 'right' : 'left',
              direction: isInputRtl ? 'rtl' : 'ltr',
              unicodeBidi: 'plaintext',
              border: 'none',
              outline: 'none'
            },
            fieldGroup: {
              direction: isInputRtl ? 'rtl' : 'ltr',
              unicodeBidi: 'plaintext',
              border: 'none',
              outline: 'none'
            }
          }}
        />
        
        <div
          className={styles.questionInputSendButtonContainer}
          role="button"
          tabIndex={0}
          aria-label={isLoading ? "Stop generation" : "Ask question button"}
          onClick={handleButtonClick}
          onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? handleButtonClick() : null)}>
          {isLoading ? (
            <StopRegular className={styles.questionInputStopButton} />
          ) : sendQuestionDisabled ? (
            <SendRegular className={styles.questionInputSendButtonDisabled} />
          ) : (
            <SendRegular className={styles.questionInputSendButton} />
          )}
        </div>
      </div>
    </div>
  )
})
